import dayjs from 'dayjs'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { API } from '@/api'
import { ItemPrice } from '@/api/type-response'
import { Button, Loading, message, Modal, Radio, Trans } from '@/components'
import { ItemPriceType } from '@/consts/item'
import { ORDER_CATALOG } from '@/consts/order'
import { ExchargeModal } from '@/pages/billing/components/RechargeModal'
import { type PlanAction, PlanConfirmModal } from '@/pages/space-mgr/components/expansion-modal'
import WarningInfoBlock from '@/pages/space-mgr/components/WarningInfoBlock.tsx'
import { useTeamStore } from '@/stores/team'
import { useUserStore } from '@/stores/user'
import { formatPrice } from '@/utils/formatter'
import { formatGB } from '@/utils/storage'
import { cn } from '@/utils/style'

const PAYMENT_TYPE = {
  1: 30,
  default: 30,
}

function formatPackagePrice(prices: ItemPrice[]) {
  if (!prices || !prices.length) return {}

  const originPrice = prices.find((p) => p.type === ItemPriceType.Original)
  const firstPrice = prices.find((p) => p.type === ItemPriceType.First)
  const renewPrice = prices.find((p) => p.type === ItemPriceType.Renew)
  const upgradePrice = prices.find((p) => p.type === ItemPriceType.Upgrade)
  const lastPrice = prices[prices.length - 1]

  return {
    originPrice,
    firstPrice,
    renewPrice,
    upgradePrice,
    lastPrice,
  }
}

interface StorageItemData {
  id: number
  capacity: number
  currentPrice: number
  renewPrice: number
  originPrice: number
  period: number
}

interface ItemProps {
  data: StorageItemData
  currentId: number | null
  originalId: number | null
  onChange: (id: number) => void
}

function Item({ data, currentId, originalId, onChange }: ItemProps) {
  return (
    <div
      onClick={() => onChange(data.id)}
      className={cn(
        'w-full px-4 py-3 rounded-2xl gap-3 justify-start border border-neutral-300 hover:bg-btn-secondary/5 hover:border-secondary focus:border-secondary focus:bg-btn-secondary/5 transition-colors duration-300 cursor-pointer',
      )}
    >
      <div className="flex justify-between items-start">
        <div className="font-bold text-[20px] leading-[28px] text-primary">
          {formatGB(data.capacity / 1024, { dynamicDecimals: true })}
        </div>
        <Radio checked={data.id === currentId} />
      </div>
      <div className="gap-2 flex">
        <div className="t-sm text-primary">
          {formatPrice(data.id === originalId ? data.renewPrice : data.currentPrice)}
        </div>
        <div className="t-sm text-tertiary">
          {data.period} <Trans i18nKey="teamSpace.admin.upgradeModal.days">Days</Trans>
        </div>
      </div>
    </div>
  )
}

interface TeamStorageUpgradeModalProps {
  open: boolean
  onClose: () => void
  onSuccess?: () => void
  initialAutoRenewal?: boolean
}

export function TeamStorageUpgradeModal({
  open,
  onClose,
  onSuccess,
  initialAutoRenewal,
}: TeamStorageUpgradeModalProps) {
  const [items, setItems] = useState<StorageItemData[]>([])
  const [selectedItem, setSelectedItem] = useState<StorageItemData | null>(null)
  const [originalId, setOriginalId] = useState<number | null>(null)
  const [currentId, setCurrentId] = useState<number | null>(null)
  const [loading, setLoading] = useState(false)
  const [order, setOrder] = useState<any>(null)
  const [autoRenewal, setAutoRenewal] = useState(false)
  const [rechargeModalOpen, setRechargeModalOpen] = useState(false)
  const [originalStorage, setOriginalStorage] = useState(0)
  const [selectedItemStorage, setSelectedItemStorage] = useState(0)
  const [confirming, setConfirming] = useState(false)

  const { t } = useTranslation()
  const teamStore = useTeamStore()
  const userStore = useUserStore()
  const teamResID = teamStore.teamTokenData?.teamResID
  const planAction: PlanAction = !order
    ? 'purchase'
    : originalStorage === selectedItemStorage
      ? 'renewal'
      : originalStorage < selectedItemStorage
        ? 'upgrade'
        : 'downgrade'
  const payableAmount =
    planAction === 'renewal'
      ? (selectedItem?.renewPrice ?? 0)
      : planAction === 'downgrade'
        ? 0
        : (selectedItem?.currentPrice ?? 0)
  const isNeedCharge = userStore.account.totalBonus.moneyCent < payableAmount

  const getDeadlineDate = () => {
    if (!order?.deadlineTime?.seconds) return null

    return dayjs(order.deadlineTime.seconds * 1000)
  }

  const getCurrentExpirationDate = () => getDeadlineDate()?.format('YYYY-MM-DD') ?? '-'

  const getNewExpirationDate = () => {
    const deadlineDate = getDeadlineDate()

    if (!order || !deadlineDate) return dayjs().add(30, 'day').format('YYYY-MM-DD')
    if (planAction === 'renewal') {
      return deadlineDate
        .add(selectedItem?.period || PAYMENT_TYPE.default, 'day')
        .format('YYYY-MM-DD')
    }

    return deadlineDate.format('YYYY-MM-DD')
  }

  const getButtonLabel = () => {
    if (isNeedCharge) {
      return <Trans i18nKey="teamSpace.admin.upgradeModal.recharge">Recharge</Trans>
    }

    if (!order) {
      return <Trans i18nKey="teamSpace.admin.upgradeModal.checkout">Checkout</Trans>
    }

    if (originalStorage === selectedItemStorage) {
      return <Trans i18nKey="teamSpace.admin.upgradeModal.checkout">Checkout</Trans>
    }

    return originalStorage < selectedItemStorage ? (
      <Trans i18nKey="teamSpace.admin.upgradeModal.upgradePlan">Upgrade Plan</Trans>
    ) : (
      <Trans i18nKey="teamSpace.admin.upgradeModal.downgradePlan">Downgrade Plan</Trans>
    )
  }

  const handleButtonClick = () => {
    if (isNeedCharge) {
      setRechargeModalOpen(true)
    } else {
      setConfirming(true)
    }
  }

  useEffect(() => {
    if (selectedItem) {
      setSelectedItemStorage(Number(selectedItem.capacity))
    }
  }, [selectedItem])

  useEffect(() => {
    if (open && teamResID) {
      setConfirming(false)
      fetchStoragePlans()
    }
  }, [open, teamResID])

  // Sync autoRenewal with initialAutoRenewal when modal opens
  useEffect(() => {
    if (open) {
      setAutoRenewal(initialAutoRenewal ?? false)
    }
  }, [open, initialAutoRenewal])

  const fetchStoragePlans = async () => {
    if (!teamResID) return

    try {
      // Fetch existing orders
      const ordersResponse = await API.getOrders({ catalog: ORDER_CATALOG.TEAM_SHARED_STORAGE })
      const orders = ordersResponse?.orders || []
      const existingOrder = orders[orders.length - 1]
      setOrder(existingOrder)
      setAutoRenewal(existingOrder?.autoRenewal || false)
      setOriginalStorage(Number(existingOrder?.item?.desc?.storage?.capacityMB || 0))

      // Build request params
      const extParams: Record<string, any> = {}
      if (existingOrder) {
        extParams.from_order_id = existingOrder.id
        extParams.from_order = true
      }

      // Fetch available items
      const itemsResponse = await API.getItems({
        page: 1,
        per_page: 1000,
        catalog: ORDER_CATALOG.TEAM_SHARED_STORAGE,
        domain: 0,
        ...extParams,
      })

      const formattedItems: StorageItemData[] = itemsResponse.items
        .map((item: any) => {
          const {
            originPrice,
            lastPrice: currentPrice,
            renewPrice,
          } = formatPackagePrice(item.prices || [])
          return {
            id: item.id,
            capacity: item.desc.storage.capacityMB,
            currentPrice: currentPrice?.priceCent ?? 0,
            originPrice: originPrice?.priceCent ?? 0,
            renewPrice: renewPrice?.priceCent ?? 0,
            period: (PAYMENT_TYPE as any)[item.paymentType] || PAYMENT_TYPE.default,
          }
        })
        .sort((a, b) => a.capacity - b.capacity)

      setItems(formattedItems)

      if (existingOrder) {
        const currentItem = formattedItems.find((item: any) => item.id === existingOrder.item.id)
        setOriginalId(currentItem?.id || null)
        setCurrentId(currentItem?.id || null)
        setSelectedItem(currentItem || null)
      } else {
        setOriginalId(null)
        setCurrentId(formattedItems[0]?.id || null)
        setSelectedItem(formattedItems[0] || null)
      }
    } catch (error) {
      console.error('Failed to fetch storage plans:', error)
    }
  }

  const handlePurchase = async () => {
    if (!currentId) return

    setLoading(true)
    try {
      const extParams: Record<string, any> = {}
      if (order) {
        extParams.modify = order.item.id !== currentId
      }

      const res = await API.purchaseStoragePlan({
        item_id: currentId,
        auto_renewal: autoRenewal,
        ...extParams,
      })

      if (res.code === 0) {
        let autoRenewalSynced = true
        const planChanged = planAction === 'upgrade' || planAction === 'downgrade'
        if (order && planChanged && order.autoRenewal !== autoRenewal) {
          try {
            await API.updateOrderAutoRenewal({
              auto_renewal: autoRenewal,
              catalog: ORDER_CATALOG.TEAM_SHARED_STORAGE,
              type: 1,
            })
          } catch (error) {
            console.error('Failed to sync team auto renewal after storage plan change:', error)
            autoRenewalSynced = false
          }
        }

        if (autoRenewalSynced) {
          message.success(
            order
              ? t('teamSpace.admin.upgradeModal.planModifiedSuccess')
              : t('teamSpace.admin.upgradeModal.planPurchasedSuccess'),
          )
        } else {
          message.error(t('teamSpace.admin.upgradeModal.autoRenewalUpdateFailed'))
        }
        onClose()
        onSuccess?.()
      }
    } catch (error: any) {
      message.error(error.msg || t('teamSpace.admin.upgradeModal.planModificationFailed'))
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      <Modal
        className="w-[800px]"
        open={open}
        onClose={() => {
          if (!loading) {
            onClose()
          }
        }}
      >
        <Modal.Header>
          <Trans i18nKey="teamSpace.admin.upgradeModal.title">Select Team Storage Plan</Trans>
        </Modal.Header>
        <Modal.Body>
          {items.length > 0 ? (
            <>
              {order && selectedItemStorage !== originalStorage && (
                <WarningInfoBlock
                  _originalStorage={originalStorage}
                  _selectedItemStorage={selectedItemStorage}
                />
              )}
              <div className="max-h-[200px] flex-row flex-wrap justify-start gap-4 grid grid-cols-3 overflow-y-auto">
                {items.map((item) => (
                  <Item
                    key={`item.${item.id}`}
                    data={item}
                    currentId={currentId}
                    originalId={originalId}
                    onChange={(id) => {
                      setCurrentId(id)
                      setSelectedItem(item)
                    }}
                  />
                ))}
              </div>
            </>
          ) : (
            <div className="w-full flex items-center justify-center">
              <Loading />
            </div>
          )}

          <div className="mt-4">
            <h3 className="text-primary mb-2">
              <Trans i18nKey="teamSpace.admin.upgradeModal.autoRenewalTitle">Auto Renewal</Trans>
            </h3>

            <div className="flex items-center gap-2">
              <div className="flex-1 px-4 py-3 rounded-lg border border-neutral-400 cursor-pointer">
                <Radio
                  checked={!autoRenewal}
                  onChange={() => setAutoRenewal(false)}
                  className="t-sm text-primary"
                >
                  <Trans i18nKey="teamSpace.admin.upgradeModal.autoRenewalNo">No</Trans>
                </Radio>
              </div>
              <div className="flex-1 px-4 py-3 rounded-lg border border-neutral-400 cursor-pointer">
                <Radio
                  checked={autoRenewal}
                  onChange={() => setAutoRenewal(true)}
                  className="t-sm text-primary"
                >
                  <Trans i18nKey="teamSpace.admin.upgradeModal.autoRenewalYes">Yes</Trans>
                </Radio>
              </div>
            </div>
          </div>

          <h3 className="text-primary mt-4 mb-2">
            <Trans i18nKey="teamSpace.admin.upgradeModal.summary">Summary</Trans>
          </h3>

          <table className="table">
            <tbody>
              <tr className="h-[44px]">
                <td className="w-[120px] text-secondary t-sm !px-0 !py-3" align="left">
                  <Trans i18nKey="teamSpace.admin.upgradeModal.storage">Storage</Trans>
                </td>
                <td className="text-primary t-sm !px-0 !py-3">
                  {selectedItem?.capacity
                    ? formatGB(selectedItem.capacity / 1024, { dynamicDecimals: true })
                    : '-'}
                </td>
              </tr>
              <tr className="h-[44px]">
                <td className="w-[120px] text-secondary t-sm !px-0 !py-3" align="left">
                  <Trans i18nKey="teamSpace.admin.upgradeModal.expire">Expires on</Trans>
                </td>
                <td className="text-primary t-sm !px-0 !py-3">
                  {!order
                    ? dayjs().add(30, 'day').format('YYYY-MM-DD')
                    : originalId === selectedItem?.id
                      ? dayjs(order.deadlineTime.seconds * 1000)
                          .add(30, 'day')
                          .format('YYYY-MM-DD')
                      : dayjs(order.deadlineTime.seconds * 1000).format('YYYY-MM-DD')}
                </td>
              </tr>
            </tbody>
          </table>
        </Modal.Body>
        <Modal.Footer className="justify-between">
          <div className="flex items-center space-x-2">
            <div className="h4 text-highlight">{formatPrice(payableAmount)}</div>
            <div className="rounded-full text-secondary py-2 px-3 bg-neutral-50 t-x-sb">
              <Trans i18nKey="teamSpace.admin.upgradeModal.teamBalance">Team Balance</Trans>
              <span className="text-primary ml-2">
                {formatPrice(userStore.account.totalBonus.moneyCent)}
              </span>
            </div>
          </div>
          <div className="space-x-4">
            <Button
              type="outline"
              onClick={() => {
                if (!loading) {
                  onClose()
                }
              }}
            >
              <Trans i18nKey="common.confirmModalCancel">Cancel</Trans>
            </Button>

            <Button
              type="secondary"
              disabled={typeof currentId === 'undefined' || currentId === null || loading}
              onClick={handleButtonClick}
            >
              {getButtonLabel()}
            </Button>
          </div>
        </Modal.Footer>
      </Modal>

      <PlanConfirmModal
        open={confirming}
        loading={loading}
        action={planAction}
        currentStorage={
          originalStorage ? formatGB(originalStorage / 1024, { dynamicDecimals: true }) : '-'
        }
        selectedStorage={
          selectedItem?.capacity
            ? formatGB(selectedItem.capacity / 1024, { dynamicDecimals: true })
            : '-'
        }
        currentExpirationDate={getCurrentExpirationDate()}
        newExpirationDate={getNewExpirationDate()}
        amountDue={payableAmount}
        onClose={() => {
          if (!loading) {
            setConfirming(false)
          }
        }}
        onConfirm={handlePurchase}
      />

      {rechargeModalOpen && (
        <ExchargeModal
          onOk={() => {
            setRechargeModalOpen(false)
            userStore.getUserInfo()
          }}
          onClose={() => {
            setRechargeModalOpen(false)
          }}
        />
      )}
    </>
  )
}
